import { useTranslation } from 'react-i18next';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import type { BudgetStatus } from '@/lib/budget';

interface BudgetProgressBarProps {
  status: BudgetStatus | null;
  compact?: boolean;
}

export function BudgetProgressBar({ status, compact = false }: BudgetProgressBarProps) {
  const { t, i18n } = useTranslation(['groups']);
  if (!status) return null;

  const {
    budgetAmountMinor,
    spentAmountMinor,
    remainingAmountMinor,
    percentage,
    currency,
    period,
    budgetStartDay,
    periodStart,
    periodEnd,
    isOverBudget,
    isApproachingLimit,
    isApproximate,
  } = status;

  const fillWidth = Math.min(percentage, 100);
  const fillColor = isOverBudget
    ? 'bg-red-500'
    : isApproachingLimit
    ? 'bg-amber-500'
    : 'bg-emerald-500';

  const spentColor = isOverBudget ? 'text-red-600 dark:text-red-400' : 'text-text-primary';
  // Monthly budgets that start on payday show the actual date range instead of "This month"
  const formatDay = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const labelText = period === 'monthly'
    ? (budgetStartDay !== 1 ? `${formatDay(periodStart)} – ${formatDay(periodEnd)}` : t('budgetProgress.thisMonth'))
    : t('budgetProgress.total');
  const approximateMark = isApproximate ? '~' : '';

  return (
    <div className={compact ? 'text-xs' : 'text-sm'}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-text-tertiary">{labelText}</span>
        <span className="flex items-baseline gap-1">
          <span className={`font-medium ${spentColor}`}>
            {approximateMark}
            <AmountDisplay amountMinor={spentAmountMinor} currency={currency} />
          </span>
          <span className="text-text-quaternary">/</span>
          <AmountDisplay
            amountMinor={budgetAmountMinor}
            currency={currency}
            className="text-text-tertiary"
          />
        </span>
      </div>

      <div
        className={`mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-tertiary`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${fillColor}`}
          style={{ width: `${fillWidth}%` }}
        />
      </div>

      {!compact && isOverBudget && (
        <div className="mt-1 text-xs text-red-600 dark:text-red-400">
          {t('web:budgetProgressBar.overBudgetBy')}{' '}
          <AmountDisplay amountMinor={-remainingAmountMinor} currency={currency} />
        </div>
      )}
      {!compact && !isOverBudget && (
        <div className="mt-1 text-xs text-text-tertiary">
          <AmountDisplay amountMinor={remainingAmountMinor} currency={currency} /> {t('web:budgetProgressBar.remaining')}
        </div>
      )}
    </div>
  );
}
